import type { ReactNode } from 'react';
import {
  BarChart3,
  CalendarDays,
  HardDrive,
  MessageSquare,
  Mic,
  MousePointer2,
  Users,
  Video,
  type LucideIcon,
} from 'lucide-react';
import type { BillingPlansResponse } from '@vd/types';
import type { SubscriptionState } from './useBillingSummary';
import { meterState, runOutDay, type Meter } from './addonOffers';
import { formatResetTime } from './billingDisplay';
import styles from './Billing.module.css';
import { formatWhen } from '../../lib/time';

interface UsageCard {
  key: string;
  label: string;
  Icon: LucideIcon;
  tone: string | undefined;
  used: number;
  limit: number | null;
  unit: string;
  note: string;
  rawUsed?: number;
  rawLimit?: number | null;
  action?: ReactNode;
  meter?: Meter;
}
const gb = (bytes: number) => Math.round((bytes / 1024 ** 3) * 1000) / 1000;
export function BillingUsage({
  sub,
  details,
  getMore,
  onWidget,
}: {
  sub: SubscriptionState;
  details: BillingPlansResponse | undefined;
  getMore: (meter: Meter) => ReactNode;
  onWidget: () => void;
}) {
  const rows: UsageCard[] = [];
  const r = sub.recording;
  if (r) {
    const breakdown = (
      included: number | null | undefined,
      boost: number | undefined,
      pack: number | undefined,
    ) =>
      included == null || (!boost && !pack)
        ? ''
        : ` ${included.toLocaleString()} included${boost ? `, ${boost.toLocaleString()} boost` : ''}${pack ? `, ${pack.toLocaleString()} from packs` : ''}.`;
    rows.push(
      {
        key: 'recording',
        label: 'Walkthrough recording',
        Icon: Video,
        tone: styles.blue,
        used: r.recordedMinutes,
        limit: r.recordingMinutes,
        unit: 'min',
        meter: 'recording',
        note: `Up to ${r.maxRecordingMinutes} minutes per recording.${breakdown(r.included?.recordingMinutes, r.boosts?.recordingMinutes, r.packs?.recordingMinutes)}`,
        action: getMore('recording'),
      },
      {
        key: 'narration',
        label: 'Narration',
        Icon: Mic,
        tone: styles.violet,
        used: r.transcribedMinutes,
        limit: r.transcriptionMinutes,
        unit: 'min',
        meter: 'narration',
        note: `Transcription and the AI ticket write-up.${breakdown(r.included?.transcriptionMinutes, r.boosts?.transcriptionMinutes, r.packs?.transcriptionMinutes)}`,
        action: getMore('narration'),
      },
      {
        key: 'storage',
        label: 'Video storage',
        Icon: HardDrive,
        tone: styles.cyan,
        used: gb(r.storageBytes),
        limit: r.storageLimitBytes === null ? null : gb(r.storageLimitBytes),
        unit: 'GB',
        rawUsed: r.storageBytes,
        rawLimit: r.storageLimitBytes,
        meter: 'storage',
        note: `Walkthrough video kept ${r.videoRetentionDays} days. Storage does not reset monthly.`,
        action: getMore('storage'),
      },
    );
  }
  if (sub.widget && !sub.widget.tierOnly) {
    const w = sub.widget;
    rows.push(
      {
        key: 'reports',
        label: 'Widget reports',
        Icon: MessageSquare,
        tone: styles.magenta,
        used: w.reportsUsed,
        limit: w.reportsAllowance,
        unit: 'reports',
        note:
          w.reportsPauseAt !== null && w.reportsUsed >= w.reportsPauseAt
            ? 'Widget paused until the monthly reset.'
            : w.reportsAllowance !== null && w.reportsUsed >= w.reportsAllowance
              ? `Reports arrive without AI titles; the widget pauses at ${w.reportsPauseAt?.toLocaleString() ?? 'its safety cap'}.`
              : 'Per workspace, per month; separate from internal tickets.',
        action: (
          <button className={styles.textLink} onClick={onWidget}>
            Manage widget capacity
          </button>
        ),
      },
      {
        key: 'visitor',
        label: 'Visitor recording',
        Icon: MousePointer2,
        tone: styles.amber,
        used: w.visitorMinutesUsed,
        limit: w.visitorMinutes,
        unit: 'min',
        note: 'Per workspace, per month; separate from member recording.',
        action: (
          <button className={styles.textLink} onClick={onWidget}>
            Manage visitor capacity
          </button>
        ),
      },
    );
  }
  if (details)
    rows.push({
      key: 'members',
      label: 'Internal members',
      Icon: Users,
      tone: styles.green,
      used: details.memberCount,
      limit: sub.seatsPurchased,
      unit: 'seats',
      note: 'Current internal members against purchased seats. Recording and storage allowances are pooled on Team and Business.',
    });
  const reset = r?.resetsAt ?? details?.usageResetsAt ?? sub.ticketsResetsAt;
  return (
    <>
      <div className={styles.sectionHeading}>
        <div>
          <h2>
            <span className={`${styles.iconTile} ${styles.blue}`}>
              <BarChart3 size={18} />
            </span>
            Usage this month
          </h2>
          <p className={styles.muted}>Current workspace consumption and purchased allowances.</p>
        </div>
        {reset && (
          <span className={styles.caption}>
            <CalendarDays aria-hidden="true" size={15} />
            Resets {formatResetTime(reset)}
          </span>
        )}
      </div>
      {sub.plan === 'free' &&
        sub.ticketsUsedThisMonth !== null &&
        typeof sub.ticketsCap === 'number' && (
          <section className={styles.card}>
            <h3>Free ticket allowance</h3>
            <p className={styles.muted}>
              You have{' '}
              <strong>
                {Math.max(0, sub.ticketsCap - sub.ticketsUsedThisMonth)} of {sub.ticketsCap} free
                tickets remaining
              </strong>{' '}
              this month
              {sub.ticketsResetsAt ? ` (resets ${formatResetTime(sub.ticketsResetsAt)})` : ''}.
            </p>
            {sub.ticketsUsedThisMonth >= sub.ticketsCap && (
              <p className={styles.muted}>
                The monthly free cap is reached. New tickets are blocked until the reset, or upgrade
                for unlimited internal tickets.
              </p>
            )}
          </section>
        )}
      <div className={styles.meterGrid}>
        {rows.map((row) => (
          <MeterCard key={row.key} row={row} />
        ))}
      </div>
      {rows.length === 0 && (
        <p className={styles.muted}>Usage details are not available for this workspace.</p>
      )}
      <p className={styles.footnote}>
        Minutes and widget submissions reset monthly, including on annual plans. Storage is current
        retained storage and does not reset each month. Walkthrough video follows its retention
        window; tickets, screenshots and transcripts are kept until deleted.
      </p>
      {sub.widget?.tierOnly && (
        <div className={`${styles.notice} ${styles.warning}`}>
          A widget subscription is still recorded for this Free workspace.{' '}
          <button className={styles.textLink} onClick={onWidget}>
            Review widget subscription
          </button>
        </div>
      )}
    </>
  );
}
function MeterCard({ row }: { row: UsageCard }) {
  const used = row.rawUsed ?? row.used;
  const limit = row.rawLimit === undefined ? row.limit : row.rawLimit;
  const state =
    row.key === 'members' && used <= (limit ?? Infinity) ? 'ok' : meterState(used, limit);
  const left =
    row.limit === null
      ? null
      : row.rawUsed !== undefined
        ? Math.max(0, gb((row.rawLimit ?? 0) - row.rawUsed))
        : Math.max(0, Math.round((row.limit - row.used) * 10) / 10);
  const forecast =
    state === 'warn' && row.meter && row.meter !== 'storage'
      ? runOutDay(row.used, row.limit)
      : null;
  return (
    <section
      className={`${styles.meterCard} ${row.tone}`}
      data-testid={`meter-${row.key}`}
      data-state={state}
    >
      <h3 className={styles.eyebrow}>
        <span className={styles.iconTile}>
          <row.Icon aria-hidden="true" size={18} />
        </span>
        {row.label}
      </h3>
      <div className={styles.meterAmount}>
        {row.used.toLocaleString()}
        <small>
          {' '}
          / {row.limit === null ? 'unlimited' : row.limit.toLocaleString()} {row.unit}
        </small>
      </div>
      {row.limit !== null && (
        <progress
          max={Math.max(1, limit ?? 1)}
          value={Math.min(used, limit ?? used)}
          aria-label={`${row.label} used`}
        />
      )}
      <div className={styles.meterMeta}>
        <span>
          {left === null ? 'No fixed allowance' : `${left.toLocaleString()} ${row.unit} remaining`}
        </span>
        {state === 'full' && <span>Limit reached</span>}
      </div>
      <p className={styles.meterNote}>{row.note}</p>
      {forecast && (
        <p className={styles.meterNote} data-testid={`meter-${row.key}-forecast`}>
          At this pace it runs out around{' '}
          {formatWhen(forecast, { dateOnly: true })}
          .
        </p>
      )}
      {row.action}
    </section>
  );
}
